import { useCallback, useEffect, useRef, useState } from "react";
import { validateEmail } from "../../shared/validationUtils";
import { sharedAttributionVisitorId, useAttributionVisitor } from "../../shared/attribution/useAttributionVisitor";

export const blankAttendee = () => ({ first_name: "", last_name: "", email: "", phone: "", note_to_instructor: "" });

const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 15;

const FINAL_STATES = ["paid", "canceled", "failed", "released"];

const STRIPE_RETURN_PARAMS = ["payment_intent", "payment_intent_client_secret", "redirect_status"];

const urlParam = (name) => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
};

const clearStripeReturnParams = () => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  STRIPE_RETURN_PARAMS.forEach((param) => url.searchParams.delete(param));
  window.history.replaceState(null, "", url.toString());
};

const PAID_HERE_PREFIX = "classCheckoutPaidHere:";

const paidHereBefore = (token) => {
  if (!token) return false;
  try {
    return window.sessionStorage.getItem(`${PAID_HERE_PREFIX}${token}`) === "1";
  } catch (error) {
    return false;
  }
};

const rememberPaidHere = (token) => {
  if (!token) return;
  try {
    window.sessionStorage.setItem(`${PAID_HERE_PREFIX}${token}`, "1");
  } catch (error) {
    return;
  }
};

const PENDING_PREFIX = "classCheckoutPending:";
const HOLD_ENDED_STATES = ["released", "failed"];

const pendingOrderToken = (key) => {
  try {
    return window.sessionStorage.getItem(`${PENDING_PREFIX}${key}`);
  } catch (error) {
    return null;
  }
};

const rememberPendingOrder = (key, token) => {
  try {
    if (token) window.sessionStorage.setItem(`${PENDING_PREFIX}${key}`, token);
    else window.sessionStorage.removeItem(`${PENDING_PREFIX}${key}`);
  } catch (error) {
    return;
  }
};

const clearUrlParam = (name) => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete(name);
  window.history.replaceState(null, "", url.toString());
};

const initialStep = (token, paymentSubmitted) => {
  if (!token) return "attendees";
  return paymentSubmitted ? "confirmation" : "resuming";
};

const setUrlParam = (name, value) => {
  if (typeof window === "undefined" || !value) return;
  const url = new URL(window.location.href);
  url.searchParams.set(name, value);
  window.history.replaceState(null, "", url.toString());
};

export const attendeeIncomplete = (attendee) =>
  !attendee.first_name.trim() || !attendee.last_name.trim() || !attendee.email.trim();

export const attendeeFieldErrors = (attendee) => {
  const errors = {};
  if (!attendee.first_name.trim()) errors.first_name = "Enter a first name.";
  if (!attendee.last_name.trim()) errors.last_name = "Enter a last name.";
  if (!attendee.email.trim()) errors.email = "Enter an email address.";
  else if (!validateEmail(attendee.email.trim())) errors.email = "Enter an email address like name@example.com.";
  return errors;
};

export const PROMO_CODE_REMOVED_MESSAGE = "This promo code can't be used any more, so we removed it.";

export default function useClassRegistrationCheckout({ initialQuantity, maxQuantity, quotes: initialQuotes, ordersUrl, placeOrderUrl, quotesUrl, initialAttendee, onOrderPlaced = () => {}, fetchImpl = fetch }) {
  const holdKey = placeOrderUrl || ordersUrl;
  const resumedFromHold = useRef(!urlParam("order") && Boolean(pendingOrderToken(holdKey)));
  const resumedToken = useRef(urlParam("order") || pendingOrderToken(holdKey));
  const redirectStatus = useRef(urlParam("redirect_status"));
  const paymentSubmitted = useRef(redirectStatus.current === "succeeded" || urlParam("paid") === "1");
  const [paidHereOnLoad] = useState(() => redirectStatus.current === "succeeded" || paidHereBefore(resumedToken.current));
  const paidInThisBrowser = useRef(paidHereOnLoad);
  const [step, setStep] = useState(initialStep(resumedToken.current, paymentSubmitted.current));
  const [quotes, setQuotes] = useState(initialQuotes);
  const [promoQuotes, setPromoQuotes] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [attendees, setAttendees] = useState(() =>
    Array.from({ length: Math.max(1, Math.min(initialQuantity, Math.max(maxQuantity, 1))) }, (_, index) => (index === 0 && initialAttendee ? { ...blankAttendee(), ...initialAttendee } : blankAttendee()))
  );
  const [subscribeToVenue, setSubscribeToVenue] = useState(false);
  const [errors, setErrors] = useState([]);
  const [priceChanged, setPriceChanged] = useState(false);
  const [capacityLost, setCapacityLost] = useState(null);
  const [working, setWorking] = useState(false);
  const [order, setOrder] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [orderMissing, setOrderMissing] = useState(false);
  const [resumeFailed, setResumeFailed] = useState(false);
  const [resumeAttempt, setResumeAttempt] = useState(0);
  const mounted = useRef(true);
  const attributionVisitorUuid = useAttributionVisitor("od_embed.js");

  useEffect(() => {
    clearStripeReturnParams();
    if (resumedFromHold.current) setUrlParam("order", resumedToken.current);
    if (redirectStatus.current === "succeeded") {
      rememberPendingOrder(holdKey, null);
      setUrlParam("paid", "1");
      rememberPaidHere(resumedToken.current);
    }
    return () => { mounted.current = false; };
  }, []);

  const settledState = (state) => (FINAL_STATES.includes(state) ? state : null);

  const quote = (promoQuotes || quotes).find((candidate) => candidate.quantity === attendees.length) || null;
  const appliedPromoCode = promoQuotes ? (promoQuotes.find((candidate) => candidate.promo_code_name) || {}).promo_code_name || null : null;

  const applyPromoCode = async (code) => {
    const trimmed = (code || "").trim();
    if (!quotesUrl || !trimmed || applyingPromo) return false;
    setApplyingPromo(true);
    setPromoError(null);
    try {
      const response = await fetchImpl(`${quotesUrl}?${new URLSearchParams({ promo_code: trimmed })}`, { headers: { Accept: "application/json" } });
      const data = await response.json().catch(() => ({}));
      if (!mounted.current) return false;
      if (!response.ok) {
        setPromoError((data.errors && data.errors[0]) || "We couldn't check that promo code. Please try again.");
        return false;
      }
      setPromoQuotes(data.quotes);
      return true;
    } catch (error) {
      if (mounted.current) setPromoError("We couldn't reach the server. Check your connection and try again.");
      return false;
    } finally {
      if (mounted.current) setApplyingPromo(false);
    }
  };

  const removePromoCode = () => {
    setPromoQuotes(null);
    setPromoError(null);
  };
  const summary = step === "payment" && order ? order : quote;
  const priced = orderStatus || order || (step === "attendees" ? quote : null);
  const free = Boolean(priced) && Number(priced.total) === 0;

  const addAttendee = () => {
    if (attendees.length >= maxQuantity) return;
    setAttendees((rows) => [...rows, blankAttendee()]);
  };

  const removeAttendee = (index) => {
    setAttendees((rows) => (rows.length > 1 ? rows.filter((_, rowIndex) => rowIndex !== index) : rows));
  };

  const updateAttendee = (index, field, value) => {
    setAttendees((rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));
  };

  const placeOrder = async (obtainVerificationToken = async () => null) => {
    if (working || !quote || attendees.some(attendeeIncomplete)) return null;
    setWorking(true);
    setErrors([]);
    setPriceChanged(false);
    setCapacityLost(null);
    let recaptchaToken;
    try {
      recaptchaToken = await obtainVerificationToken();
    } catch (error) {
      if (mounted.current) {
        setErrors(["We couldn't verify this browser. Please try again."]);
        setWorking(false);
      }
      return null;
    }
    try {
      const response = await fetchImpl(placeOrderUrl || ordersUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          class_registration_order: { attendees, expected_total: quote.total, subscribe_to_venue: subscribeToVenue, promo_code_name: appliedPromoCode },
          recaptcha_token: recaptchaToken,
          attribution_visitor_uuid: attributionVisitorUuid || sharedAttributionVisitorId() || "",
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!mounted.current) return null;
      if (!response.ok) {
        const repriced = data.code === "price_changed" && Array.isArray(data.quotes);
        setPriceChanged(repriced);
        if (repriced && appliedPromoCode && data.quotes.some((candidate) => candidate.promo_code_name)) {
          setPromoQuotes(data.quotes);
        } else if (repriced) {
          setQuotes(data.quotes);
          if (appliedPromoCode) {
            setPromoQuotes(null);
            setPromoError(PROMO_CODE_REMOVED_MESSAGE);
          }
        }
        if (data.code === "capacity_exhausted" && data.spots_left === 0) setCapacityLost({ waitlistOpen: Boolean(data.waitlist_open) });
        setErrors(data.errors || [data.error_description || "We couldn't start your registration. Please try again."]);
        return null;
      }
      setUrlParam("order", data.token);
      rememberPendingOrder(holdKey, data.state === "paid" ? null : data.token);
      setOrder(data);
      if (data.state === "paid") {
        paidInThisBrowser.current = true;
        rememberPaidHere(data.token);
      }
      setStep(data.state === "paid" ? "confirmation" : "payment");
      onOrderPlaced(data);
      return data;
    } catch (error) {
      if (mounted.current) setErrors(["We couldn't reach the server. Check your connection and try again."]);
      return null;
    } finally {
      if (mounted.current) setWorking(false);
    }
  };

  const orderToken = order?.token || resumedToken.current;

  const fetchOrder = (token, { complete }) => (complete
    ? fetchImpl(`${ordersUrl}/${token}/completion`, { method: "POST", headers: { Accept: "application/json" } })
    : fetchImpl(`${ordersUrl}/${token}`, { headers: { Accept: "application/json" } }));

  const pollStatus = useCallback(async (isCancelled = () => !mounted.current) => {
    if (!orderToken) return;
    setPollTimedOut(false);
    let completed = !paymentSubmitted.current;
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
      try {
        const response = await fetchOrder(orderToken, { complete: !completed });
        if (isCancelled()) return;
        if (response.status === 404) {
          setOrderMissing(true);
          return;
        }
        if (!response.ok) throw new Error(`status ${response.status}`);
        completed = true;
        const data = await response.json();
        if (isCancelled()) return;
        setOrderStatus(data);
        if (settledState(data.state)) return;
      } catch (error) {
        if (isCancelled()) return;
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    if (!isCancelled()) setPollTimedOut(true);
  }, [fetchImpl, orderToken, ordersUrl]);

  useEffect(() => {
    if (step !== "confirmation") return undefined;
    let ignore = false;
    pollStatus(() => ignore || !mounted.current);
    return () => { ignore = true; };
  }, [step, pollStatus]);

  useEffect(() => {
    if (step !== "resuming") return undefined;
    let ignore = false;
    (async () => {
      try {
        const response = await fetchOrder(resumedToken.current, { complete: true });
        if (ignore) return;
        const data = response.ok ? await response.json() : null;
        if (ignore) return;
        if (resumedFromHold.current && (response.status === 404 || HOLD_ENDED_STATES.includes(data?.state))) {
          resumedFromHold.current = false;
          resumedToken.current = null;
          rememberPendingOrder(holdKey, null);
          clearUrlParam("order");
          setStep("attendees");
          return;
        }
        if (resumedFromHold.current && data && !data.stripe_client_secret) rememberPendingOrder(holdKey, null);
        if (response.status === 404) {
          setOrderMissing(true);
          setStep("confirmation");
          return;
        }
        if (!response.ok) throw new Error(`status ${response.status}`);
        if (data.stripe_client_secret) {
          setOrder({ ...data, token: resumedToken.current });
          if (redirectStatus.current === "failed") setErrors(["Your payment didn't go through. Try again or use a different card."]);
          setStep("payment");
        } else {
          setOrderStatus(data);
          setStep("confirmation");
        }
      } catch (error) {
        if (!ignore) setResumeFailed(true);
      }
    })();
    return () => { ignore = true; };
  }, [step, resumeAttempt, fetchImpl, ordersUrl]);

  const retryResume = () => {
    setResumeFailed(false);
    setResumeAttempt((attempt) => attempt + 1);
  };

  const onPaymentSuccess = () => {
    rememberPendingOrder(holdKey, null);
    paymentSubmitted.current = true;
    paidInThisBrowser.current = true;
    rememberPaidHere(orderToken);
    setUrlParam("paid", "1");
    setStep("confirmation");
  };

  return {
    step,
    attendees,
    addAttendee,
    removeAttendee,
    updateAttendee,
    subscribeToVenue,
    setSubscribeToVenue,
    quote,
    summary,
    free,
    appliedPromoCode,
    applyPromoCode,
    removePromoCode,
    applyingPromo,
    promoError,
    errors,
    priceChanged,
    capacityLost,
    working,
    order,
    orderStatus,
    pollTimedOut,
    orderMissing,
    resumeFailed,
    settled: orderStatus ? settledState(orderStatus.state) : null,
    paidInThisBrowser: paidInThisBrowser.current,
    retryResume,
    pollStatus,
    placeOrder,
    onPaymentSuccess,
  };
}
